import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { getUltimaVentaQR } from "../services/visitsheets.service";
import { guardarFechaDocLeido } from "../services/fincas.service";
import Logo from "../images/LogoLAE.png";
import LanguageSwitcher from "./LanguageSwitcher";

// Clave para recordar que el usuario venia a ver los informes cuando se le manda a iniciar sesion
const PENDIENTE_INFORMES = "qr-informes-pendiente";

// Van en public/ y no en src/images para poder reemplazarlas sin recompilar.
// Si falta alguna la pagina se ve igual, solo que con el degradado a secas
const IMG = {
  inicio: `${process.env.PUBLIC_URL}/qr/fondo_inicio.jpg`,
  cae: `${process.env.PUBLIC_URL}/qr/banner_cae.jpg`,
  fondoOmnia: `${process.env.PUBLIC_URL}/qr/fondo_omnia_hogar.jpg`,
  bannerOmnia: `${process.env.PUBLIC_URL}/qr/banner_omnia_hogar.jpg`,
};

const AZUL = "#1b3a5f";
const GRANATE = "#92004d";
const MORADO = "#992add";
const GRIS_SECCION = "#f4f4f6";
const GRIS_FUERA = "#e8e8ec";
// Degradado de los botones, tomado de la plantilla
const DEGRADADO_BOTON = "linear-gradient(90deg, #a30250 0%, #7a185c 55%, #602866 100%)";
const DEGRADADO_BOTON_HOVER = "linear-gradient(90deg, #8a0243 0%, #64144c 55%, #4d2054 100%)";
// 100vh cuenta de mas en el movil, porque no descuenta la barra del navegador.
// dvh mide el alto que de verdad se ve; queda vh de respaldo para navegadores viejos
// dvh es el alto que de verdad se ve en cada momento: se reajusta cuando la barra
// del navegador aparece o desaparece, asi la pantalla se ve igual en ambos casos.
// Con svh (alto fijo con la barra desplegada) sobraba sitio al esconderse la barra
// y asomaba el bloque siguiente; con lvh pasaba lo contrario y se salia el pie.
const ALTO_PANTALLA = {
  minHeight: "100vh",
  "@supports (min-height: 1dvh)": { minHeight: "100dvh" },
};


// La pagina se diseña para movil; en pantallas grandes se centra con este ancho
const ANCHO_MOVIL = 460;

const esProveedor = () => {
  try {
    return jwt_decode(localStorage.getItem("token"))?.rol === "SUPP";
  } catch (e) {
    return false;
  }
};

const haySesionIniciada = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    return jwt_decode(token).exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
};

// Columna centrada: en movil ocupa todo, en escritorio simula la pantalla del telefono
const Pantalla = ({ children, sx }) => (
  <Box sx={{ ...ALTO_PANTALLA, bgcolor: GRIS_FUERA, display: "flex", justifyContent: "center" }}>
    <Box
      sx={{
        width: "100%",
        maxWidth: ANCHO_MOVIL,
        ...ALTO_PANTALLA,
        bgcolor: "#fff",
        boxShadow: { xs: "none", sm: "0 0 40px rgba(0,0,0,0.12)" },
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...sx,
      }}
    >
      {/* Selector de idioma, comun a todas las vistas. El idioma elegido se guarda y la
          plataforma lo usa al abrir la hoja de visita, que sale en ese mismo idioma */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 2,
          // El boton del selector viene muy bajo para ir en una barra; aqui flota solo
          "& .MuiButton-root": { ml: 0, minHeight: 40, minWidth: 44, px: 1.5 },
          // En el movil el codigo del idioma va oculto: sin su hueco, el globo queda centrado
          "& .MuiButton-startIcon": { ml: 0, mr: { xs: 0, sm: 1 } },
        }}
      >
        <LanguageSwitcher forceShow />
      </Box>
      {children}
    </Box>
  </Box>
);

const TituloSeccion = ({ children, sx }) => (
  <Typography component="div"
    sx={{
      color: GRANATE,
      fontWeight: 800,
      textTransform: "uppercase",
      fontSize: "1.05rem",
      letterSpacing: "0.01em",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const Subtitulo = ({ children }) => (
  <Typography component="div" sx={{ color: GRANATE, fontWeight: 700, fontSize: "0.95rem", mt: 4, mb: 1.5 }}>
    {children}
  </Typography>
);

const Parrafo = ({ children, sx }) => (
  <Typography component="div" sx={{ color: "#3f3f3f", fontSize: "0.92rem", lineHeight: 1.7, textAlign: "justify", mb: 2, ...sx }}>
    {children}
  </Typography>
);

const BotonPrincipal = ({ children, sx, ...props }) => (
  <Button
    variant="contained"
    disableElevation
    sx={{
      borderRadius: 1.5,
      py: 1.7,
      px: 4,
      textTransform: "uppercase",
      fontWeight: 700,
      fontSize: "1rem",
      letterSpacing: "0.02em",
      lineHeight: 1.3,
      backgroundImage: DEGRADADO_BOTON,
      color: "#fff !important",
      boxShadow: "0 5px 12px rgba(0,0,0,0.28)",
      "&:hover": { backgroundImage: DEGRADADO_BOTON_HOVER },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Button>
);

export default function QR() {
  const { t } = useTranslation("qr");
  const { token } = useParams();
  const [venta, setVenta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  // inicio | proveedores | vecinos
  const [vista, setVista] = useState("inicio");
  const navigate = useNavigate();
  const location = useLocation();
  const logueado = haySesionIniciada();
  const detalleRef = useRef(null);
  const documentosRef = useRef(null);

  useEffect(() => {
    const fetchNif = async () => {
      setCargando(true);
      setError(null);
      try {
        const res = await getUltimaVentaQR(token);
        const datos = res?.data;
        // Sin venta CAE la pantalla se muestra igual, solo que sin informes
        if (!datos?.nif) {
          setError(true);
        } else {
          setVenta(datos);
        }
      } catch (e) {
        setError(true);
      } finally {
        setCargando(false);
      }
    };
    fetchNif();
  }, [token]);

  // Cada espacio deja una entrada en el historial para que el boton atras del movil
  // devuelva a la eleccion en lugar de salir de la landing
  const abrirVista = (nueva) => {
    setVista(nueva);
    window.scrollTo(0, 0);
    window.history.pushState({ vistaQR: nueva }, "");
  };

  const volverAInicio = () => {
    if (window.history.state?.vistaQR) {
      window.history.back();
    } else {
      setVista("inicio");
    }
  };

  // El QR puede estar pegado en la finca antes de la visita; hasta entonces no hay informes
  const hayVisita = !!venta?.visitaRealizada && !!venta?.sale_id;

  useEffect(() => {
    // Sin esto, el rebote del scroll o el crecer del viewport enseña el body en blanco
    const antes = document.body.style.backgroundColor;
    document.body.style.backgroundColor = GRIS_FUERA;
    return () => { document.body.style.backgroundColor = antes; };
  }, []);

  useEffect(() => {
    const alRetroceder = () => setVista("inicio");
    window.addEventListener("popstate", alRetroceder);
    return () => window.removeEventListener("popstate", alRetroceder);
  }, []);

  // Al volver del login se abren directamente los informes que el usuario intentaba consultar
  useEffect(() => {
    if (logueado && sessionStorage.getItem(PENDIENTE_INFORMES) === token) {
      sessionStorage.removeItem(PENDIENTE_INFORMES);
      setVista("proveedores");
      window.history.pushState({ vistaQR: "proveedores" }, "");
    }
  }, [logueado, token]);

  // Los documentos CAE son los mismos de la plataforma, por lo que exigen sesion iniciada
  const urlDocumento = (tipo) =>
    venta?.workcenter_id
      ? `/doc-render/${venta.nif}/${venta.workcenter_id}/${venta.sale_id}/${tipo}`
      : `/doc-render/${venta?.nif}/${venta?.sale_id}/${tipo}`;

  // Igual que en la ficha de la comunidad del proveedor: abrir el documento lo marca como
  // leido. Solo cuenta para proveedores; el enlace se abre igual aunque falle el registro
  const marcarLeido = (doc_id) => {
    if (!doc_id || !esProveedor()) return;
    guardarFechaDocLeido(doc_id).catch(() => {});
  };

  const irALogin = () => {
    sessionStorage.setItem(PENDIENTE_INFORMES, token);
    navigate("/login", { state: { from: { pathname: location.pathname } } });
  };

  const irAlDetalle = () => detalleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  // "Ver documentacion" baja hasta los informes, no al principio de la explicacion
  const irADocumentos = () => documentosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (cargando) {
    return (
      <Pantalla sx={{ alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: GRANATE }} />
        <Typography component="div" sx={{ mt: 2, color: AZUL }}>{t("loading")}</Typography>
      </Pantalla>
    );
  }

  if (error) {
    return (
      <Pantalla sx={{ alignItems: "center", justifyContent: "center", p: 4, textAlign: "center" }}>
        <img src={Logo} alt="GrupoLae" style={{ height: 100, objectFit: "contain", marginBottom: 32 }} />
        <QrCodeScannerIcon sx={{ fontSize: 56, color: "#b0b0b0", mb: 2 }} />
        <Typography component="div" sx={{ color: GRANATE, fontWeight: 800, fontSize: "1.3rem", mb: 2 }}>
          {t("errors.invalidTitle")}
        </Typography>
        <Typography component="div" sx={{ color: "#555", lineHeight: 1.6 }}>{t("errors.invalidText")}</Typography>
      </Pantalla>
    );
  }

  // ---------- Pantalla de bienvenida ----------
  if (vista === "inicio") {
    return (
      <Pantalla
        sx={{
          // La imagen ya trae el edificio y las manchas de color. Se ajusta al ancho
          // en lugar de recortarse, para que el edificio salga como en el diseño
          backgroundImage: `url(${IMG.inicio})`,
          backgroundColor: "#e6edf7",
          backgroundSize: "100% auto",
          backgroundPosition: "center bottom",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* El contenido se reparte sobre el alto visible, no sobre el del documento:
            si no, con la barra del navegador desplegada el pie se queda fuera */}
        <Box
          sx={{
            ...ALTO_PANTALLA,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 6,
            pb: 4,
            textAlign: "center",
          }}
        >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <img src={Logo} alt="GrupoLae" style={{ height: 118, objectFit: "contain" }} />

          <Typography component="div" sx={{ color: GRANATE, fontWeight: 800, textTransform: "uppercase", fontSize: "1.95rem", mt: 4 }}>
            {t("home.welcome")}
          </Typography>

          <Typography component="div" sx={{ color: AZUL, fontSize: "1.05rem", lineHeight: 1.55, mt: 2, whiteSpace: "pre-line" }}>
            {t("home.intro")}
          </Typography>
        </Box>

        {/* Los botones caen en el tercio inferior, sobre la foto */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
          <BotonPrincipal fullWidth onClick={() => abrirVista("proveedores")}>
            {t("home.providers")}
          </BotonPrincipal>
          <BotonPrincipal fullWidth onClick={() => abrirVista("vecinos")}>
            {t("home.neighbours")}
          </BotonPrincipal>
        </Box>

        <Typography component="div" sx={{ color: AZUL, fontWeight: 700, fontSize: "0.95rem" }}>
          {venta?.direccion ? t("home.community", { direccion: venta.direccion }) : venta?.comunidad}
        </Typography>
        </Box>
      </Pantalla>
    );
  }

  const enVecinos = vista === "vecinos";
  const conOmnia = enVecinos && !!venta?.mostrarOmniaHogar;

  // ---------- Cabecera con el estado de la visita ----------
  const Cabecera = () => (
    <Box sx={{ position: "relative", flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          flex: "1 1 auto",
          // La imagen ya viene con el cielo claro arriba, donde caen logo y titular.
          // Al ancho y pegada abajo, el edificio queda justo encima del texto
          backgroundImage: `url(${IMG.cae})`,
          backgroundColor: "#e4ecf6",
          backgroundSize: "100% auto",
          backgroundPosition: "center bottom",
          backgroundRepeat: "no-repeat",
          pt: 4,
          pb: 7,
          px: 2,
          textAlign: "center",
        }}
      >
        <img src={Logo} alt="GrupoLae" style={{ height: 100, objectFit: "contain" }} />
        <Typography component="div"
          sx={{
            mt: 3,
            color: AZUL,
            fontWeight: 800,
            textTransform: "uppercase",
            lineHeight: 1.3,
            fontSize: "1.4rem",
            whiteSpace: "pre-line",
          }}
        >
          {hayVisita ? t("header.visitDone") : t("header.visitPending")}{" "}
          <Box component="span" sx={{ color: GRANATE, display: "block" }}>
            {hayVisita ? t("header.visitDoneHighlight") : t("header.visitPendingHighlight")}
          </Box>
        </Typography>
      </Box>
      {/* Arranca dentro de la foto y sigue por debajo, como en el diseño */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: -30,
          width: "1px",
          height: 104,
          bgcolor: GRANATE,
          zIndex: 1,
        }}
      />
    </Box>
  );

  // ---------- Banner de Omnia Hogar ----------
  const BannerOmnia = () => (
    <Box
      sx={{
        ...ALTO_PANTALLA,
        display: "flex",
        flexDirection: "column",
        // El fondo ya trae las manchas de color del diseño
        backgroundImage: `url(${IMG.fondoOmnia})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
      }}
    >
      <Box sx={{ pt: 4, pb: 2, textAlign: "center" }}>
        <img src={Logo} alt="GrupoLae" style={{ height: 110, objectFit: "contain" }} />
      </Box>
      {/* La imagen llega sin texto: el reclamo va encima en HTML para poder traducirlo */}
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={IMG.bannerOmnia}
          alt=""
          sx={{ width: "100%", display: "block", aspectRatio: "1441 / 1117", objectFit: "cover" }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: "58%",
            pl: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "left",
          }}
        >
          <Typography component="div" sx={{ color: AZUL, fontWeight: 800, fontSize: "1.5rem", lineHeight: 1.15, whiteSpace: "pre-line" }}>
            {t("omniaBanner.title")}
          </Typography>
          <Typography component="div" sx={{ color: MORADO, fontWeight: 800, fontSize: "1.5rem", lineHeight: 1.15, whiteSpace: "pre-line" }}>
            {t("omniaBanner.titleHighlight")}
          </Typography>
          <Typography component="div" sx={{ color: AZUL, fontSize: "0.98rem", lineHeight: 1.35, mt: 1.5, whiteSpace: "pre-line" }}>
            <Trans i18nKey="omniaBanner.subtitle" ns="qr" components={{ b: <strong /> }} />
          </Typography>
        </Box>
      </Box>
      <Box sx={{ textAlign: "center", px: 3, pt: 4, pb: 4, flex: "1 1 auto", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Button
          href="https://omnia-hogar.com/"
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          disableElevation
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.9,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1.15rem",
            backgroundColor: MORADO,
            color: "#fff !important",
            boxShadow: "0 5px 14px rgba(0,0,0,0.25)",
            "&:hover": { backgroundColor: "#8420c0" },
          }}
        >
          {t("omnia")}
        </Button>
        <Typography component="div" sx={{ color: AZUL, mt: 3.5, fontSize: "0.98rem", lineHeight: 1.6 }}>
          <Trans
            i18nKey={hayVisita ? "neighbours.visitDoneInline" : "neighbours.visitPendingInline"}
            ns="qr"
            components={{ b: <strong /> }}
          />
        </Typography>
        <ArrowDownwardIcon sx={{ color: AZUL, fontSize: 34, mt: 3.5, mx: "auto" }} />
      </Box>
    </Box>
  );

  // Ocupa el alto visible del movil, de modo que lo de abajo queda fuera hasta hacer scroll
  const PrimeraPantalla = ({ children, sx }) => (
    <Box sx={{ ...ALTO_PANTALLA, display: "flex", flexDirection: "column", ...sx }}>{children}</Box>
  );

  const BotonVolver = () => (
    <Box sx={{ px: 3, py: 5 }}>
      <Button
        fullWidth
        variant="outlined"
        onClick={volverAInicio}
        sx={{
          borderRadius: 2,
          py: 1.6,
          color: "#666",
          borderColor: "#d5d5d5",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "1rem",
          "&:hover": { borderColor: "#bbb", backgroundColor: "transparent" },
        }}
      >
        {t("back")}
      </Button>
    </Box>
  );

  const BloqueDetalle = ({ children }) => (
    <Box ref={detalleRef} sx={{ bgcolor: GRIS_SECCION, mx: 2, mt: 3, borderRadius: 3, px: 3, py: 5 }}>
      {children}
    </Box>
  );

  // ---------- Espacio de vecinos ----------
  if (enVecinos) {
    return (
      <Pantalla>
        {conOmnia ? (
          <BannerOmnia />
        ) : (
          <PrimeraPantalla>
            <Cabecera />
            <Box sx={{ textAlign: "center", px: 3, pt: 5, pb: 5 }}>
              <Typography component="div" sx={{ color: AZUL, fontWeight: 600, fontSize: "1.2rem", lineHeight: 1.55, mb: 4 }}>
                {t("neighbours.intro")}
              </Typography>
              <BotonPrincipal onClick={irAlDetalle}>{t("neighbours.whatIsCaeButton")}</BotonPrincipal>
            </Box>
          </PrimeraPantalla>
        )}

        <BloqueDetalle>
          <TituloSeccion sx={{ mb: 3 }}>{t("neighbours.whatIsCae")}</TituloSeccion>
          <Parrafo>
            <Trans i18nKey={hayVisita ? "neighbours.summary" : "neighbours.summaryPending"} ns="qr" components={{ b: <strong /> }} />
          </Parrafo>

          <Subtitulo>{t("neighbours.whyVisit")}</Subtitulo>
          <Parrafo>
            <Trans i18nKey="neighbours.whyVisitText" ns="qr" components={{ b: <strong />, br: <br /> }} />
          </Parrafo>
          <Parrafo>
            <Trans i18nKey="neighbours.mainGoal" ns="qr" components={{ b: <strong /> }} />
          </Parrafo>

          <Subtitulo>{t("neighbours.obligations")}</Subtitulo>
          <Parrafo>
            <Trans i18nKey="neighbours.obligationsText" ns="qr" components={{ b: <strong />, br: <br /> }} />
          </Parrafo>

          <Subtitulo>{t("neighbours.needDocs")}</Subtitulo>
          <Parrafo sx={{ mb: 0 }}>
            <Trans i18nKey="neighbours.needDocsText" ns="qr" components={{ b: <strong />, br: <br /> }} />
          </Parrafo>
        </BloqueDetalle>

        <BotonVolver />
      </Pantalla>
    );
  }

  // ---------- Espacio de proveedores ----------
  return (
    <Pantalla>
      <PrimeraPantalla>
      <Cabecera />

      <Box sx={{ textAlign: "center", px: 3, pt: 5, pb: 5 }}>
        <TituloSeccion sx={{ mb: 2 }}>{t("providers.implies")}</TituloSeccion>
        <Typography component="div" sx={{ color: AZUL, fontSize: "1rem", lineHeight: 1.6 }}>
          <Trans i18nKey="providers.impliesSummary" ns="qr" components={{ b: <strong /> }} />
        </Typography>
        <Typography
          component="button"
          type="button"
          onClick={irAlDetalle}
          sx={{
            mt: 2.5,
            mb: 3,
            display: "block",
            mx: "auto",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: AZUL,
            fontWeight: 700,
            textDecoration: "underline",
            textUnderlineOffset: "3px",
            textTransform: "uppercase",
            fontSize: "1rem",
            letterSpacing: "0.02em",
          }}
        >
          {t("providers.moreInfo")}
        </Typography>
        <BotonPrincipal onClick={irADocumentos}>{t("providers.seeDocs")}</BotonPrincipal>
      </Box>
      </PrimeraPantalla>

      <BloqueDetalle>
        <TituloSeccion sx={{ mb: 3 }}>{t("providers.implies")}</TituloSeccion>
        <Parrafo>
          <Trans i18nKey="providers.whatImpliesText" ns="qr" components={{ b: <strong />, br: <br /> }} />
        </Parrafo>
        <Parrafo>
          <Trans i18nKey="providers.mainGoal" ns="qr" components={{ b: <strong /> }} />
        </Parrafo>

        <Subtitulo>{t("providers.whyNeeded")}</Subtitulo>
        <Parrafo>
          <Trans i18nKey="providers.whyNeededText" ns="qr" components={{ b: <strong />, br: <br /> }} />
        </Parrafo>

        <Subtitulo>{t("providers.whatIsCae")}</Subtitulo>
        <Parrafo>
          <Trans i18nKey="providers.whatIsCaeText" ns="qr" components={{ b: <strong /> }} />
        </Parrafo>

        <Subtitulo>{t("providers.whatMeans")}</Subtitulo>
        <Parrafo sx={{ mb: 1 }}>{t("providers.whatMeansIntro")}</Parrafo>
        <Box component="ul" sx={{ pl: 3, m: 0, mb: 2, color: "#3f3f3f", fontSize: "0.92rem", lineHeight: 1.7 }}>
          <li>
            <Trans i18nKey="providers.bullet1" ns="qr" components={{ b: <strong /> }} />
          </li>
          <li>
            <Trans i18nKey="providers.bullet2" ns="qr" components={{ b: <strong /> }} />
          </li>
          <li>
            <Trans i18nKey="providers.bullet3" ns="qr" components={{ b: <strong /> }} />
          </li>
        </Box>
        <Parrafo>
          <Trans i18nKey="providers.closing" ns="qr" components={{ b: <strong /> }} />
        </Parrafo>

        <Box ref={documentosRef} sx={{ mt: 6, scrollMarginTop: "16px" }}>
          <TituloSeccion sx={{ mb: 3 }}>{t("providers.documents")}</TituloSeccion>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {!logueado ? (
            <>
              <Parrafo sx={{ textAlign: "center", mb: 1 }}>{t("providers.loginNeeded")}</Parrafo>
              <BotonPrincipal fullWidth onClick={irALogin}>
                {t("providers.login")}
              </BotonPrincipal>
            </>
          ) : !hayVisita ? (
            <Parrafo sx={{ textAlign: "center", mb: 0 }}>{t("providers.noDocuments")}</Parrafo>
          ) : (
            <>
              <BotonPrincipal fullWidth href={urlDocumento("hoja-visita")} target="_blank" rel="noopener noreferrer">
                {t("providers.visitSheet")}
              </BotonPrincipal>
              <BotonPrincipal
                fullWidth
                href={urlDocumento("identificacion-riesgos")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => marcarLeido(venta?.documentos?.identificacionRiesgos)}
              >
                {t("providers.riskIdentification")}
              </BotonPrincipal>
              <BotonPrincipal
                fullWidth
                href={urlDocumento("medidas-emergencia")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => marcarLeido(venta?.documentos?.medidasEmergencia)}
              >
                {t("providers.emergencyMeasures")}
              </BotonPrincipal>
            </>
          )}
        </Box>
      </BloqueDetalle>

      <BotonVolver />
    </Pantalla>
  );
}
